import { useState, useEffect, useRef, type ReactNode } from 'react';
import type { PretextLayoutResult } from '../../lib/pretext/types';

interface EditorialFlowProps {
  /** Text blocks to lay out around the obstacle */
  text: string;
  /** Font string for pretext measurement */
  font?: string;
  /** Line height multiplier */
  lineHeight?: number;
  /** Children rendered as the obstacle (e.g., a photo) */
  children?: ReactNode;
  /** Additional className */
  className?: string;
}

/**
 * Editorial text flow that wraps around an obstacle using pretext.
 * Falls back to standard CSS layout when pretext is unavailable.
 *
 * Approved use cases (doc 04):
 * - Landing hero proof area
 * - Landing sample result area
 * - Results editorial coaching area
 */
export function EditorialFlow({
  text,
  font,
  lineHeight = 1.6,
  children,
  className = '',
}: EditorialFlowProps) {
  const [layout, setLayout] = useState<PretextLayoutResult | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!font || !containerRef.current) return;

    let cancelled = false;
    const width = containerRef.current.offsetWidth;

    import('../../lib/pretext/measure').then(({ measureTextWithLines }) => {
      measureTextWithLines(text, font, width, 16 * lineHeight).then((result) => {
        if (!cancelled && result) setLayout(result);
      });
    }).catch(() => {
      // Fallback: pretext unavailable, use CSS layout
    });

    return () => { cancelled = true; };
  }, [text, font, lineHeight]);

  // Always render the fallback-safe CSS layout.
  // Pretext measurements enhance but don't replace the DOM structure.
  return (
    <div ref={containerRef} className={`editorial-flow ${className}`}>
      {children && (
        <div className="float-right ml-6 mb-4 max-w-[50%]">
          {children}
        </div>
      )}
      <div
        className="font-ui text-ink-muted leading-relaxed"
        data-pretext-lines={layout?.lineCount}
      >
        {layout?.lines ? (
          layout.lines.map((line, i) => (
            <span key={i} className="block">
              {line.text}
            </span>
          ))
        ) : (
          <p>{text}</p>
        )}
      </div>
    </div>
  );
}
